/**
 * Symbol normalization utilities for the frontend.
 * This provides client-side symbol normalization to ensure consistent data
 * before sending to the backend.
 */

// Common ticker mistakes - maps wrong symbols to correct ones
const TICKER_CORRECTIONS = {
  'NIKE': 'NKE',
  'GOOGLE': 'GOOGL',
  'FACEBOOK': 'META',
  'FB': 'META',
  'TWITTER': 'X',
  'TWTR': 'X',
  'BERKSHIRE': 'BRK-B',
  'ALPHABET': 'GOOGL',
};

/**
 * Normalize stock symbol for API calls.
 * This is the SINGLE SOURCE OF TRUTH for symbol normalization on the frontend.
 * 
 * Handles:
 * - Exchange prefixes (e.g., "NASDAQ:AAPL" -> "AAPL")
 * - Period to hyphen conversion (e.g., "BRK.B" -> "BRK-B")
 * - Common ticker mistakes (e.g., "NIKE" -> "NKE")
 * - Whitespace and case normalization
 * 
 * @param {string} symbol - Raw stock symbol from any source
 * @returns {string} Normalized symbol ready for API calls
 */
export const normalizeSymbol = (symbol) => {
  if (!symbol || typeof symbol !== 'string') {
    return '';
  }
  
  // Step 1: Basic cleanup - uppercase and trim whitespace
  let normalized = symbol.toUpperCase().trim();
  
  // Step 2: Strip exchange prefix if present (e.g., "NASDAQ:AAPL" -> "AAPL")
  if (normalized.includes(':')) {
    normalized = normalized.split(':').pop().trim();
  }
  
  // Step 3: Convert periods to hyphens (yfinance expects "BRK-B" not "BRK.B")
  normalized = normalized.replace(/\./g, '-');
  
  // Step 4: Remove any invalid characters (keep only alphanumeric and hyphen)
  normalized = normalized.replace(/[^A-Z0-9-]/g, '');
  
  // Step 5: Check for common ticker mistakes
  if (TICKER_CORRECTIONS[normalized]) {
    console.log(`Corrected ticker '${normalized}' to '${TICKER_CORRECTIONS[normalized]}'`);
    normalized = TICKER_CORRECTIONS[normalized];
  }
  
  return normalized;
};

/**
 * Validate and normalize a stock symbol.
 * 
 * @param {string} symbol - Raw stock symbol
 * @returns {{ normalized: string, isValid: boolean }} - Normalized symbol and validity
 */
export const validateSymbol = (symbol) => {
  const normalized = normalizeSymbol(symbol);
  
  if (!normalized) {
    return { normalized: '', isValid: false };
  }
  
  // Basic validation - must be 1-10 characters, alphanumeric with optional hyphen
  const isValid = normalized.length >= 1 && 
                  normalized.length <= 10 && 
                  /^[A-Z][A-Z0-9-]{0,9}$/.test(normalized);
  
  return { normalized, isValid };
};

export default normalizeSymbol;
